/**
 * Enhances imported blog article markup (plain <p>/<h2>/<img>/<ul>) so it can be
 * styled like the design without editors having to add classes by hand:
 *
 * - A heading whose text is wrapped in quote marks becomes a pull quote.
 * - A list where every item starts with <strong>Label:</strong> becomes a stat box.
 * - Bare embed iframes (Spotify, YouTube, Vimeo, …) are wrapped in `.embed-wrapper`.
 *
 * Classes that editors already added (stat-box, references-box, embed-wrapper) are respected.
 */
class BlogPostContent extends HTMLElement {
  static QUOTE_START = /^["“‘']/;

  connectedCallback() {
    this.#markQuotes();
    this.#markStatLists();
    this.#wrapEmbeds();
  }

  #markQuotes() {
    for (const heading of this.querySelectorAll('h1, h2, h3, h4')) {
      if (BlogPostContent.QUOTE_START.test(heading.textContent?.trim() ?? '')) {
        heading.classList.add('blog-post-quote');
      }
    }
  }

  #markStatLists() {
    for (const list of this.querySelectorAll('ul:not(.stat-box), ol:not(.stat-box)')) {
      const items = Array.from(list.children);
      if (items.length === 0) continue;

      const labels = items.map((item) => {
        const first = item.firstElementChild;
        const startsWithStrong =
          first?.matches('strong, b') && item.textContent?.trim().startsWith(first.textContent?.trim() ?? '');
        return startsWithStrong ? first : null;
      });
      if (labels.some((label) => !label)) continue;

      list.classList.add('stat-box', 'stat-box--list');
      items.forEach((item, index) => {
        const label = /** @type {HTMLElement} */ (labels[index]);
        item.classList.add('stat-box__row');
        label.classList.add('stat-box__label');
        label.textContent = (label.textContent ?? '').trim().replace(/:$/, '');
      });
    }
  }

  #wrapEmbeds() {
    for (const embed of this.querySelectorAll('iframe, video')) {
      if (embed.closest('.embed-wrapper')) continue;

      const wrapper = document.createElement('div');
      wrapper.className = 'embed-wrapper';

      const src = embed.getAttribute('src') ?? '';
      if (embed.tagName === 'VIDEO' || /youtube|youtu\.be|vimeo|wistia|loom/.test(src)) {
        wrapper.classList.add('embed-wrapper--video');
      }

      const target = embed.parentElement?.tagName === 'P' && embed.parentElement.children.length === 1
        ? embed.parentElement
        : embed;
      target.replaceWith(wrapper);
      wrapper.append(embed);
    }
  }
}

if (!customElements.get('blog-post-content')) {
  customElements.define('blog-post-content', BlogPostContent);
}
